import axios, { type AxiosResponse } from 'axios'

import type {
    AdditionalActionsResponseSchema,
    FindTaskPayloadSchema,
    GetAdditionalActionProcessPayloadSchema,
    GetAdditionalActionReportProcessCodePayloadSchema,
    GetCommentCodesPayloadSchema,
    GetNextPlatePayloadSchema,
    GetNextPlatesPayloadSchema,
    GetScheduledTasksPayloadSchema,
    GetTaskPayloadSchema,
    NextPlatesToReportResponseSchema,
    NextPlateToReportResponseSchema,
    OperatorReportingReportItemOrdersFilters,
    ReportCommentCodesResponseSchema,
    ReportItemOrdersResponseSchema,
    ReportOrderJobsResponseSchema,
    ReportOutOfSequenceCodesResponseSchema,
    ReportProcessesResponseSchema,
    ReportTaskDetailsResponseSchema,
    ReportTaskSearchResponseSchema,
    ScheduledTasksResponseSchema,
    ShippingGroupsResponseSchema
} from '@/api/schemas/reports/operatorReporting'
import type { ReportItemColoursResponseSchema } from '@/api/schemas/reports/reportExecution'
import { API_ENDPOINTS } from '@/constants/apiEndpoints'
import { REPORTS } from '@/constants/domain/reports'

class ReportTaskDiscoveryQueries {
    getReportProcesses(productionSiteId: number): Promise<AxiosResponse<ReportProcessesResponseSchema>> {
        return axios.get<ReportProcessesResponseSchema>(API_ENDPOINTS.REPORTS.RUNTIME.REPORT_PROCESS_CODES, {
            params: {
                productionSite_id: productionSiteId
            }
        })
    }

    getScheduledTasks(payload: GetScheduledTasksPayloadSchema): Promise<AxiosResponse<ScheduledTasksResponseSchema>> {
        const { productionSiteId, machineCode, limit, sortByPriority } = payload
        const searchParams = new URLSearchParams()

        if (machineCode) searchParams.append('machCode', machineCode)
        if (limit) searchParams.append('limit', String(limit))
        if (sortByPriority !== undefined) searchParams.append('sortByPriority', String(sortByPriority))

        const queryString = searchParams.toString()
        const url = `${API_ENDPOINTS.REPORTS.RUNTIME.SCHEDULED_TASKS}/${productionSiteId}${queryString ? `?${queryString}` : ''}`

        return axios.get<ScheduledTasksResponseSchema>(url)
    }

    findTask(payload: FindTaskPayloadSchema): Promise<AxiosResponse<ReportTaskSearchResponseSchema>> {
        const { taskNumber, typeId = '', elastic } = payload

        return axios.get<ReportTaskSearchResponseSchema>(API_ENDPOINTS.REPORTS.RUNTIME.REPORT_ITEMS, {
            params: {
                primaryNumber: taskNumber,
                typeId,
                elastic: elastic ? true : undefined,
                limit: REPORTS.FIND_TASK_LIMIT
            }
        })
    }

    findTaskByNumber(payload: FindTaskPayloadSchema): Promise<AxiosResponse<ReportTaskSearchResponseSchema>> {
        const { taskNumber, typeId = '', elastic } = payload

        return axios.get<ReportTaskSearchResponseSchema>(
            `${API_ENDPOINTS.REPORTS.RUNTIME.REPORT_ITEMS_BY_NUMBER}/${taskNumber}`,
            {
                params: {
                    typeId,
                    elastic: elastic ? true : undefined,
                    limit: REPORTS.FIND_TASK_LIMIT
                }
            }
        )
    }

    getTask(payload: GetTaskPayloadSchema): Promise<AxiosResponse<ReportTaskDetailsResponseSchema>> {
        const { taskId, processId } = payload

        return axios.get<ReportTaskDetailsResponseSchema>(`${API_ENDPOINTS.REPORTS.RUNTIME.REPORT_ITEMS}/${taskId}`, {
            params: {
                productionProcess_id: processId
            }
        })
    }

    getNextPlatesToReport(payload: GetNextPlatesPayloadSchema): Promise<AxiosResponse<NextPlatesToReportResponseSchema>> {
        const { taskNumber, processId, typeId } = payload

        return axios.get<NextPlatesToReportResponseSchema>(
            `${API_ENDPOINTS.REPORTS.RUNTIME.REPORT_ITEMS_NEXT_UNREPORTED_PLATES}/${taskNumber}/${processId}/${typeId}`
        )
    }

    getNextPlateToReport(payload: GetNextPlatePayloadSchema): Promise<AxiosResponse<NextPlateToReportResponseSchema>> {
        const { processId, productionSiteId, previouslyReportedPrimaryNumber } = payload

        return axios.get<NextPlateToReportResponseSchema>(
            `${API_ENDPOINTS.REPORTS.RUNTIME.NEXT_PLATE_TO_REPORT}/${processId}/${productionSiteId}`,
            {
                params: {
                    previouslyReportedPrimaryNumber: previouslyReportedPrimaryNumber || undefined
                }
            }
        )
    }

    getShippingGroups(): Promise<AxiosResponse<ShippingGroupsResponseSchema>> {
        return axios.get<ShippingGroupsResponseSchema>(API_ENDPOINTS.REPORTS.RUNTIME.REPORT_SHIPPING_GROUPS)
    }

    getReportItemOrders(
        productionSiteId: number | string,
        filters: OperatorReportingReportItemOrdersFilters
    ): Promise<AxiosResponse<ReportItemOrdersResponseSchema>> {
        const searchParams = new URLSearchParams()

        if (filters.shippingGroupCodes) {
            searchParams.append('shippingGroupCodes', filters.shippingGroupCodes)
        }

        searchParams.append('limit', String(REPORTS.OPERATOR_REPORTING.REPORT_ITEM_ORDERS_LIMIT))

        const queryString = searchParams.toString()

        return axios.get<ReportItemOrdersResponseSchema>(
            `${API_ENDPOINTS.REPORTS.RUNTIME.REPORT_ITEM_ORDERS}/${productionSiteId}${queryString ? `?${queryString}` : ''}`
        )
    }
    getReportItemOrderJobs(queryString = ''): Promise<AxiosResponse<ReportOrderJobsResponseSchema>> {
        return axios.get<ReportOrderJobsResponseSchema>(`${API_ENDPOINTS.REPORTS.RUNTIME.REPORT_ITEM_ORDER_JOBS}${queryString}`)
    }

    getReportOutOfSequenceCodes(): Promise<AxiosResponse<ReportOutOfSequenceCodesResponseSchema>> {
        return axios.get<ReportOutOfSequenceCodesResponseSchema>(API_ENDPOINTS.REPORTS.RUNTIME.REPORT_OUT_OF_SEQUENCE_CODES)
    }

    getCommentCodes(payload: GetCommentCodesPayloadSchema): Promise<AxiosResponse<ReportCommentCodesResponseSchema>> {
        const { codeTypeId, productionSiteId } = payload

        return axios.get<ReportCommentCodesResponseSchema>(API_ENDPOINTS.REPORTS.RUNTIME.REPORT_COMMENT_CODES, {
            params: {
                codeType_id: codeTypeId,
                productionSite_id: productionSiteId
            }
        })
    }

    getAdditionalActionProcessProductionSite(
        payload: GetAdditionalActionProcessPayloadSchema
    ): Promise<AxiosResponse<AdditionalActionsResponseSchema>> {
        const { processId, productionSiteId, queryString = '' } = payload

        return axios.get<AdditionalActionsResponseSchema>(
            `${API_ENDPOINTS.REPORTS.RUNTIME.ADDITIONAL_ACTION_PROCESS_PRODUCTION_SITE}/${processId}/${productionSiteId}${queryString}`
        )
    }

    getAdditionalActionReportProcessCodeProductionSite(
        payload: GetAdditionalActionReportProcessCodePayloadSchema
    ): Promise<AxiosResponse<AdditionalActionsResponseSchema>> {
        const { processCodeId, productionSiteId, queryString = '' } = payload

        return axios.get<AdditionalActionsResponseSchema>(
            `${API_ENDPOINTS.REPORTS.RUNTIME.ADDITIONAL_ACTION_REPORT_PROCESS_CODE_PRODUCTION_SITE}/${processCodeId}/${productionSiteId}${queryString}`
        )
    }

    getReportItemColours(reportItemId: number | string): Promise<AxiosResponse<ReportItemColoursResponseSchema>> {
        return axios.get<ReportItemColoursResponseSchema>(`${API_ENDPOINTS.REPORTS.RUNTIME.REPORT_ITEM_COLOURS}/${reportItemId}`)
    }

    updateTasksPriority(payload: Record<string, unknown>): Promise<AxiosResponse<unknown>> {
        return axios.post<unknown>(API_ENDPOINTS.SCHEDULE_PRIORITIZATION.PRIORITIZED_TASKS, payload)
    }
}

export default new ReportTaskDiscoveryQueries()
